import {
  timeFormatDefaultLocale as d3TimeFormatDefaultLocale,
  select as d3Select,
  scaleLinear as d3ScaleLinear,
  scaleBand as d3ScaleBand,
  axisLeft as d3AxisLeft,
  max as d3Max,
  axisBottom as d3AxisBottom
} from 'd3'

import { checkWidthScreen } from './utils'

import { parseTimezoneDate, getJSONLocaleDate, diffInDays } from '../../utils'

/* @ngInject */
function AnalyticsDataFrameBarChartController (
  $rootScope,
  $sce,
  $scope,
  $state,
  $translate,
  $timeout,
  $attrs,
  $window,
  $location,
  Utils,
  AnalyticsService,
  SegmentService
) {
  const api = {
    _loadDataFrameData,
    _successGetResponse,
    _errorGetResponse,
    _finallyGetResponse,
    _insertParamsAndLoadData,
    _getUserLocale,
    _successGetUserLocale,

    _insertBarChart,
    _getInitialSvg,
    _topRoundedRect,
    _getGroupXWidth,
    _getBarColor,
    _getVerticalScalePosition,
    _getHorizontalScalePosition,
    _onMouseOver,
    _onMouseOut,
    _getBigNumber,
    _mountRectChartByTime,
    _removeBarChart,
    _mountDropdowns,
    _parseResponseFromAPI,
    _getXScaleDomainByType,
    _getXTickFormat,
    _getYTickFormat,
    _formatDateXTick,
    _getScrollTooltipXPosition,
    _getTooltipXPosition,
    _mountVerticalSvg,
    _handleResizeBarChart,
    _getDataFormatByDefault,
    _getDateFormatByDefault,
    _checkLineBreak,
    _isHandleDateFormat,
    _getYScaleDomain,
    _insertMetricIntoBar,
    _getBarMetricXPosition,
    _formatNumber,
    _hasXAxisHidden
  }

  const dateFormat = api._getDateFormatByDefault($scope.data.vars)
  const dataFormat = api._getDataFormatByDefault($scope.data.vars)

  $scope.flags = {
    loading: true,
    hasError: false,
    emptyBar: false,
    isFirstLoad: true,
    notHaveBarMetric: false
  }

  $scope.params = {
    start: '',
    end: '',
    dateFormat,
    dataFormat
  }

  $scope.components = {
    dropdown: [],
    legend: []
  }

  $scope.form = {
    dropdown: {
      dateFormat,
      dataFormat
    }
  }

  $scope.dataFrameSlug = ''
  $scope.description = ''

  const slug = $state.params.slug || $scope.slug

  this.getApi = () => api

  activate()

  function activate () {
    api._getUserLocale()

    const { width, x } = api._handleResizeBarChart()

    $scope.dataFrameName = $translate.instant($scope.data.name)
    $scope.dataFrameTruncateName = Utils.truncateText($translate.instant($scope.data.name), 64)

    $scope.chart = {
      svgContent: d3Select($attrs.$$element.find('.bar-chart-content')[0]),
      svgWrapper: d3Select($attrs.$$element.find('.bar-chart-content .bar-chart-wrapper')[0]),
      width,
      height: 450,
      svgVertical: {
        width: 60,
        height: 450
      },
      xAxis: {
        text: {
          dy: '16px'
        }
      },
      yAxis: {
        text: {
          dx: '-10px'
        }
      },
      group: {
        x,
        y: 85,
        withScroll: {
          x: 5
        }
      }
    }

    $scope.dataFrameSlug = $scope.data.slug
  }

  $scope.$watchCollection('filterParams', api._insertParamsAndLoadData)

  $scope.loadDataFrameData = () => {
    return api._loadDataFrameData(slug, $scope.data.id)
  }

  $scope.selectFilterChange = option => {
    const { label, value, key } = option

    $scope.params[key] = value
    $scope.form.dropdown[key] = value

    api._loadDataFrameData(slug, $scope.data.id)

    SegmentService.track('analytics-dataframe-viewchange', {
      data_frame: $scope.dataFrameSlug,
      view_name: label
    })
  }

  /**
   * This method call the method for get data of card
   *
   * @param {String} paramsSlug
   * @param {Number} dataFrameId
   */
  function _loadDataFrameData (paramsSlug, dataFrameId) {
    $scope.flags.loading = true
    $scope.loadingDataFrames.barChart = true

    $scope.flags.emptyBar = false
    $scope.flags.hasError = false

    AnalyticsService.getDataFrameDataFromId(paramsSlug, dataFrameId, $scope.params)
      .then(api._successGetResponse)
      .catch(api._errorGetResponse)
      .finally(api._finallyGetResponse)
  }

  /**
   * This method load the values when request is successful.
   *
   * check if exists data and added values to card, if not exists added empty values to card
   *
   * @param {object} response
   */
  function _successGetResponse (response) {
    const {
      data: { result }
    } = response

    if (response.dataFrameName) {
      $scope.dataFrameName = $translate.instant(response.dataFrameName)
      $scope.dataFrameTruncateName = Utils.truncateText($translate.instant(response.dataFrameName), 64)
    }

    if (response.description) {
      $scope.description = $sce.trustAsHtml(response.description)
    }

    const list = $scope.data.vars?.list || []
    const legend = $scope.data.vars?.legend || []

    if (response.replaceLegend && legend.length) {
      if (legend[0].name === 'replaceLegend') {
        legend[0].translation = response.replaceLegend
      }
    }

    $scope.components.dropdown = api._mountDropdowns(list)

    if (!$scope.components.legend.length) {
      $scope.components.legend = legend
    }

    if (!result.length) {
      $scope.flags.emptyBar = true
      $scope.flags.hasError = false
      return
    }

    api._insertBarChart(result)
  }

  /**
   * This method show the error and added empty values for when request fails
   */
  function _errorGetResponse (error) {
    console.log({ error })

    $scope.flags.hasError = true
  }

  function _finallyGetResponse () {
    $scope.flags.loading = false
    $scope.loadingDataFrames.barChart = false
  }

  /**
   * This method get new values from filter params and load the data
   *
   * Is called when filter params object is changed
   *
   * @param {object} newValues
   */
  function _insertParamsAndLoadData (newValues) {
    if (!newValues.filtersLoaded) return

    const {
      calendar: { start, end }
    } = newValues

    const { dateFormat: currentDateFormat } = $scope.form.dropdown
    const calendarDaysDiff = $scope.params.dateFormat ? diffInDays(start, end) : 0
    const newDateFormat = $scope.params.dateFormat ? api._isHandleDateFormat(calendarDaysDiff) : ''

    const isCampaign = $state.current.name.includes('campaign')

    const haveDateParameters = ($location.search()?.periodStart && $location.search()?.periodEnd) || isCampaign

    $scope.params = {
      ...$scope.params,
      ...newValues,
      dateFormat: $scope.params.dateFormat ? $scope.form.dropdown.dateFormat : '',
      start,
      end
    }

    if (haveDateParameters && newDateFormat && newDateFormat !== currentDateFormat) {
      $scope.form.dropdown.dateFormat = newDateFormat
      $scope.params.dateFormat = newDateFormat
    }

    api._loadDataFrameData(slug, $scope.data.id)
  }

  function _getUserLocale () {
    $rootScope.getUser().then(api._successGetUserLocale)
  }

  function _successGetUserLocale ({ locale }) {
    $scope.locale = locale
  }

  function _insertBarChart (responseFromAPI) {
    if ($scope.flags.isFirstLoad) {
      $scope.flags.isFirstLoad = false
    }

    const { response } = api._parseResponseFromAPI($scope.data.unitType, responseFromAPI)
    const { svg, svgVertical, svgContent, svgWrapper, height, width, hasXAxisHidden } = api._getInitialSvg(response)
    const localization = d3TimeFormatDefaultLocale(getJSONLocaleDate($scope.locale))
    const barWidth = 40

    let widthCalcForScroll = (barWidth * response.length) + width

    if ($scope.data.unitType === 'TEXT') {
      widthCalcForScroll = (barWidth * response.length * 3) + width
    }

    $scope.chart = {
      ...$scope.chart,
      xScale: {
        domain: api._getXScaleDomainByType($scope.data.unitType, response),
        range: svgVertical ? [0, widthCalcForScroll] : [0, width],
        paddingInner: svgVertical ? 0 : 0.8,
        paddingOuter: svgVertical ? 0 : 0.1
      },
      yScale: {
        range: [height, 0],
        domain: api._getYScaleDomain($scope.data.unitType, response),
        paddingInner: 0,
        paddingOuter: 0
      },
      bar: {
        width: barWidth,
        yRadius: 4,
        xRadius: 4
      }
    }

    const xScale = d3ScaleBand()
      .domain($scope.chart.xScale.domain)
      .range($scope.chart.xScale.range)
      .paddingInner($scope.chart.xScale.paddingInner)
      .paddingOuter($scope.chart.xScale.paddingOuter)

    const yScale = d3ScaleLinear()
      .domain($scope.chart.yScale.domain)
      .range($scope.chart.yScale.range)
      .nice()

    const yGroup = svg
      .append('g')
      .attr(
        'transform',
        `translate(${svgVertical ? $scope.chart.group.withScroll.x : $scope.chart.group.x}, ${$scope.chart.group.y})`
      )
      .attr('class', 'y-group')

    const xGroup = svg
      .append('g')
      .attr(
        'transform',
        `translate(${svgVertical ? $scope.chart.group.withScroll.x : $scope.chart.group.x}, ${$scope.chart.group.y})`
      )
      .attr('class', 'x-group')

    const xTicksFormat = api._getXTickFormat($scope.data.unitType, localization, response)
    const yTicksFormat = value => api._getYTickFormat($scope.data.unitType, value)

    const xAxisGenerator = d3AxisBottom(xScale)
      .tickSize(0)
      .ticks(response.length)
      .tickFormat(xTicksFormat)

    const yAxisGenerator = d3AxisLeft(yScale)
      .tickSize(-width)
      .tickFormat(yTicksFormat)

    const yAxis = yGroup
      .append('g')
      .call(yAxisGenerator)
      .attr('class', 'y-axis')

    const xAxis = xGroup
      .append('g')
      .call(xAxisGenerator)
      .attr('class', () => {
        const hiddenClass = 'x-axis x-axis-hidden'
        const defaultClass = 'x-axis'

        if (hasXAxisHidden) {
          return hiddenClass
        }

        return defaultClass
      })
      .attr('transform', `translate(0, ${height})`)

    xAxis.selectAll('text').attr('dy', $scope.chart.xAxis.text.dy)
    yAxis.selectAll('text').attr('dx', $scope.chart.yAxis.text.dx)

    // create vertical axis and updated width of svg
    api._mountVerticalSvg(svg, svgVertical, svgWrapper, yAxisGenerator, xGroup)

    const barElement = xGroup
      .selectAll('.bar')
      .data(response)
      .enter()
      .append('path')
      .attr('class', data => {
        if (data.date) {
          return `bar bar-${data.date.getTime()}`
        }

        if (!data.date && data.color) {
          return `bar bar-${data.color.replace('#', '')}`
        }

        return 'bar'
      })
      .attr('d', data => api._mountRectChartByTime(response, data, height, yScale, xScale))
      .attr('fill', api._getBarColor)

    api._insertMetricIntoBar(xGroup, response, xScale, yScale)

    barElement
      .on('mouseover', (event, data) => {
        return api._onMouseOver(event, data, height, width, svgContent, svgVertical, svgWrapper, hasXAxisHidden)
      })
      .on('mouseout', () => api._onMouseOut(svgContent))
  }

  /**
   * this method gets the bar chart elements, also check if the chart will scroll horizontally
   */
  function _getInitialSvg (response) {
    const margin = 150
    const maxItemsWithoutScroll = $scope.data.unitType === 'TEXT' ? 5 : 16

    const hasXAxisHidden = api._hasXAxisHidden($scope.data.unitType)
    const svgContent = $scope.chart.svgContent
    const svgWrapper = $scope.chart.svgWrapper
    const svgVerticalWrapper = svgContent.select('.vertical-svg')

    // for update svg
    api._removeBarChart(svgWrapper, svgVerticalWrapper)
    svgContent.classed('is-vertical', false)

    const svg = svgWrapper
      .append('svg')
      .attr('width', $scope.chart.width)
      .attr('height', $scope.chart.height)

    const svgVertical = svgVerticalWrapper
      .append('svg')
      .attr('width', $scope.chart.svgVertical.width)
      .attr('height', $scope.chart.svgVertical.height)

    const fullHeight = svg.attr('height')
    const fullWidth = svg.attr('width')

    const width = fullWidth - margin
    const height = fullHeight - margin

    if (response.length > maxItemsWithoutScroll) {
      svgVerticalWrapper.style('display', 'block')
      svgWrapper.style('overflow-x', 'scroll')
      svgContent.classed('is-vertical', true)

      return {
        svg,
        svgContent,
        svgWrapper,
        svgVertical,
        width,
        height
      }
    }

    if (hasXAxisHidden) {
      svgWrapper.style('margin-bottom', () => hasXAxisHidden ? '-40px' : '0')
    }

    svgVerticalWrapper.style('display', 'none')
    svgWrapper.style('overflow-x', 'hidden')
    svgWrapper.style('max-width', `${fullWidth}px`)
    svg.style('max-width', '100%')

    return {
      svg,
      svgContent,
      svgWrapper,
      svgVertical: null,
      width,
      hasXAxisHidden,
      height
    }
  }

  /**
   * x is equals horizontal
   * y is equals vertical
   *
   * @param {Object} data - { name: string, value: number }
   * @param {String} xScale - x-coordinate
   * @param {String} yScale - y-coordinate
   * @param {String} height - height
   * @param {String} yRadius - y-radius
   * @param {String} xRadius - x-radius
   * @returns {String} - draw string
   */
  function _topRoundedRect (data, xScale, yScale, height, yRadius, xRadius) {
    const half = 2
    const width = $scope.chart.bar.width
    const unitType = $scope.data.unitType

    const defaultXScale = () => xScale(data.date)
    const xScales = {
      TIME_DATE: defaultXScale,
      TIME_TEXT: () => {
        if ($scope.form.dropdown.dataFormat === 'STATUS') {
          return xScale($translate.instant(data.translation))
        }

        return xScale(data.name)
      },
      RATE_DATE: defaultXScale,
      NUMBER_TEXT: () => xScale(data.color)
    }

    let xScaleFromType = xScales[String(unitType)]?.() || xScales.TIME_DATE()
    if (unitType === 'TEXT') {
      xScaleFromType = xScale(data.id)
    }

    const withoutValueYPosition = yScale(data.total) - (height - ((xRadius + yRadius) / half))
    const withValueYPosition = yScale(data.total) + ((xRadius + yRadius) / half)

    const yPosition = data.total ? withValueYPosition : withoutValueYPosition
    const xPosition = xScaleFromType + (xScale.bandwidth() / half - width / half)

    const draw = `
      M${xPosition},${yPosition}
      a${xRadius},${yRadius} 0 0 1 ${xRadius},${-yRadius}
      h${width - half * xRadius}
      a${xRadius},${yRadius} 0 0 1 ${xRadius},${yRadius}
      v${height - yRadius}
      h${-width}Z
    `

    return draw
  }

  function _getGroupXWidth (xGroup) {
    const gap = 10
    const xGroupWidth = xGroup.node().getBoundingClientRect()

    return {
      width: xGroupWidth.width + gap
    }
  }

  function _getBarColor (data) {
    return data.color
  }

  function _getVerticalScalePosition (tooltipElement, data, barRectY, height, barRectHeight, hasXAxisHidden) {
    const heightTooltip = tooltipElement.node().offsetHeight
    const half = 2
    const minBarRectHeight = 30

    if (!data.total) {
      if (hasXAxisHidden) {
        const marginTop = 30

        return {
          yScalePosition: height - marginTop
        }
      }

      if ($scope.flags.notHaveBarMetric) {
        const marginTop = 10

        return {
          yScalePosition: height + marginTop
        }
      }

      const marginTop = 10

      return {
        yScalePosition: height - marginTop
      }
    }

    if (barRectHeight < minBarRectHeight) {
      return {
        yScalePosition: barRectY + (heightTooltip + barRectHeight) - barRectHeight / half
      }
    }

    return {
      yScalePosition: barRectY + barRectHeight / half
    }
  }

  function _getHorizontalScalePosition (tooltipElement, data, width, rectBarX, barRectWidth, svgWrapper, svgVertical) {
    const widthTooltip = tooltipElement.node().offsetWidth
    const widthVerticalSvg = 60
    const ninetyPercent = 0.9
    const gap = 12
    const half = 2

    if (svgVertical) {
      return api._getScrollTooltipXPosition(
        tooltipElement,
        data,
        width,
        rectBarX,
        barRectWidth,
        svgWrapper,
        widthTooltip,
        widthVerticalSvg,
        ninetyPercent,
        gap,
        half
      )
    }

    return api._getTooltipXPosition(
      tooltipElement,
      data,
      width,
      rectBarX,
      barRectWidth,
      widthTooltip,
      ninetyPercent,
      gap,
      half
    )
  }

  function _onMouseOver (event, data, height, width, svgContent, svgVertical, svgWrapper, hasXAxisHidden) {
    const tooltip = svgContent.select('.bar-chart-tooltip')
    const barRectElement = event.target

    const { formatted } = Utils.getCompleteTime(data.total)

    const tooltipTexts = {
      TIME_DATE: () => `${formatted}`,
      DATE_TIME: () => `${formatted}`,
      TIME_TEXT: () => {
        const truncateTextLimit = 15
        const breakLine = api._checkLineBreak(data)

        if ($scope.form.dropdown.dataFormat === 'STATUS') {
          return `${Utils.truncateText(
            $translate.instant(data.translation),
            truncateTextLimit
          )}: ${breakLine} ${formatted}`
        }

        return `${Utils.truncateText(data.name, truncateTextLimit)}: ${breakLine} ${formatted}`
      },
      RATE_DATE: () => {
        const parsedTotal = parseFloat(data.total)
        const parsedCount = parseFloat(data.count)

        const singularRatings = $translate.instant('ratings.singular')
        const pluralRatings = $translate.instant('ratings.plural')
        const noRatings = $translate.instant('ratings.without')

        if (parsedTotal) {
          const oneRating = 1
          const ratingsAmount = `${parsedCount} ${parsedCount === oneRating ? singularRatings : pluralRatings}`

          return ratingsAmount
        }

        return noRatings
      },
      NUMBER_TEXT: () => {
        const parsedTotal = parseFloat(data.total)

        if (parsedTotal) {
          return ''
        }

        return $translate.instant(data.emptyMessage)
      },
      TEXT: () => {
        return data.name
      }
    }

    const tooltipHtml = tooltipTexts[String($scope.data.unitType)]?.() || ''

    if (!tooltipHtml) {
      return
    }

    // update text into the tooltip
    tooltip.html(tooltipHtml)

    const { x: barRectX, y: barRectY, height: barRectHeight, width: barRectWidth } = barRectElement.getBBox()

    const { x: boundingBarRectX } = barRectElement.getBoundingClientRect()

    const yPosition = barRectY
    const xPosition = svgVertical ? boundingBarRectX : barRectX

    const { yScalePosition } = api._getVerticalScalePosition(tooltip, data, yPosition, height, barRectHeight, hasXAxisHidden)

    const { xScalePosition } = api._getHorizontalScalePosition(
      tooltip,
      data,
      width,
      xPosition,
      barRectWidth,
      svgWrapper,
      svgVertical
    )

    tooltip
      .style('left', `${xScalePosition}px`)
      .style('top', `${yScalePosition}px`)
      .style('opacity', 1)
  }

  function _onMouseOut (svgContent) {
    svgContent.select('.bar-chart-tooltip').style('opacity', 0)
  }

  function _getBigNumber (array) {
    const orderedArray = array.sort((a, b) => a.total - b.total)
    const lastIndex = orderedArray.length - 1
    const bigNumber = orderedArray[lastIndex]

    return bigNumber
  }

  function _mountRectChartByTime (response, data, height, yScale, xScale) {
    const fivePercent = 0.05
    const bigNumber = api._getBigNumber(response)
    const bigFivePercent = bigNumber.total * fivePercent

    const parsedHeight = data.total ? height - yScale(data.total) : height
    const yRadius = $scope.chart.bar.yRadius
    const xRadius = $scope.chart.bar.xRadius

    if (!data.total) {
      const minHeight = 10

      return api._topRoundedRect(data, xScale, yScale, minHeight, yRadius, xRadius)
    }

    if (data.total < bigFivePercent) {
      const newParsedHeight = height - yScale(bigFivePercent)

      const newData = {
        ...data,
        total: bigFivePercent
      }

      return api._topRoundedRect(newData, xScale, yScale, newParsedHeight, yRadius, xRadius)
    }

    return api._topRoundedRect(data, xScale, yScale, parsedHeight, yRadius, xRadius)
  }

  /**
   * This method remove the bar chart and the tooltip from the DOM
   *
   * used when update the bar chart
   *
   * @param {d3Element} elementWrapper
   */
  function _removeBarChart (elementWrapper, elementVerticalWrapper) {
    elementWrapper.select('svg').remove()
    elementVerticalWrapper.select('svg').remove()
  }

  function _mountDropdowns (dropdownList) {
    const notHaveDateParameters = !$location.search()?.periodStart && !$location.search()?.periodEnd

    if (dropdownList?.length) {
      return dropdownList.map(({ defaultValue, queryKey, queryValue, translation }) => {
        if (defaultValue === 'TRUE' && $scope.flags.isFirstLoad) {
          $scope.params[queryKey] = queryValue
        }

        return {
          key: queryKey,
          value: queryValue,
          label: $translate.instant(translation),
          active:
            (notHaveDateParameters && $scope.flags.isFirstLoad && defaultValue === 'TRUE') ||
            $scope.form.dropdown[queryKey] === queryValue
        }
      })
    }

    return []
  }

  function _parseResponseFromAPI (type, responseFromAPI) {
    const defaultResponse = () =>
      responseFromAPI.map(data => ({
        ...data,
        date: parseTimezoneDate(data.date)
      }))

    const types = {
      TIME_DATE: defaultResponse,
      TIME_TEXT: () => responseFromAPI,
      RATE_DATE: defaultResponse,
      NUMBER_TEXT: () => responseFromAPI,
      TEXT: () => responseFromAPI
    }

    return {
      response: types[String(type)]?.() || responseFromAPI
    }
  }

  function _getXScaleDomainByType (type, response) {
    const defaultDomain = () => response.map(data => data.date)

    const types = {
      TIME_DATE: defaultDomain,
      TIME_TEXT: () =>
        response.map(data => {
          if ($scope.form.dropdown.dataFormat === 'STATUS') {
            return $translate.instant(data.translation)
          }

          return data.name
        }),
      RATE_DATE: defaultDomain,
      NUMBER_TEXT: () => response.map(data => data.color),
      TEXT: () => response.map(data => data.id)
    }

    const domain = types[String(type)]?.() || defaultDomain()

    return domain
  }

  function _getXTickFormat (type, localization, response) {
    const defaultXTickFormat = () => localization.format(api._formatDateXTick())

    const types = {
      TIME_DATE: defaultXTickFormat,
      TIME_TEXT: () => null,
      RATE_DATE: defaultXTickFormat,
      NUMBER_TEXT: () => null,
      TEXT: () => (id) => {
        const text = response.find(item => item.id === id)?.name || ''

        if (response.length <= 5) {
          return text
        }

        const shortText = Utils.truncateText(text, 25)
        return shortText || text
      }
    }

    return types[String(type)]?.()
  }

  function _getYTickFormat (type, value) {
    const defaultYTickFormat = Utils.convertMinutesToDays(value)

    const types = {
      TIME_DATE: () => defaultYTickFormat,
      TIME_TEXT: () => defaultYTickFormat,
      RATE_DATE: () => {
        const parsedRate = parseFloat(value)

        if (!parsedRate) {
          return $translate.instant('dataFrames.barChart.noAvailableInitials')
        }

        return parsedRate.toFixed(1)
      },
      NUMBER_TEXT: () => parseInt(value, 10),
      TEXT: () => parseInt(value, 10)
    }

    return types[String(type)]?.()
  }

  function _formatDateXTick () {
    const dateFormat = $scope.form.dropdown.dateFormat

    const formats = {
      MONTHLY: '%b %y',
      WEEKLY: '%d %b'
    }

    return formats[String(dateFormat)] || formats.WEEKLY
  }

  function _getScrollTooltipXPosition (
    tooltipElement,
    data,
    width,
    rectBarX,
    barRectWidth,
    svgWrapper,
    widthTooltip,
    widthVerticalSvg,
    ninetyPercent,
    gap,
    half
  ) {
    const xPositionSvgWrapper = svgWrapper.node().getBoundingClientRect().x
    const xPosition = rectBarX - xPositionSvgWrapper + widthVerticalSvg

    tooltipElement.classed('bar-chart-tooltip-center', false)
    tooltipElement.classed('bar-chart-tooltip-right', false)
    tooltipElement.classed('bar-chart-tooltip-left', false)

    if (!data.total) {
      tooltipElement.classed('bar-chart-tooltip-center', true)

      const diffGap = 2
      const marginLeft = (barRectWidth - widthTooltip) / half - diffGap

      return {
        xScalePosition: xPosition + marginLeft
      }
    }

    if (xPosition > width * ninetyPercent) {
      tooltipElement.classed('bar-chart-tooltip-right', true)

      const marginRight = widthTooltip + gap

      return {
        xScalePosition: xPosition - marginRight
      }
    }

    tooltipElement.classed('bar-chart-tooltip-left', true)

    const marginLeft = barRectWidth + gap

    return {
      xScalePosition: xPosition + marginLeft
    }
  }

  function _getTooltipXPosition (
    tooltipElement,
    data,
    width,
    rectBarX,
    barRectWidth,
    widthTooltip,
    ninetyPercent,
    gap,
    half
  ) {
    tooltipElement.classed('bar-chart-tooltip-center', false)
    tooltipElement.classed('bar-chart-tooltip-right', false)
    tooltipElement.classed('bar-chart-tooltip-left', false)

    if (!data.total) {
      tooltipElement.classed('bar-chart-tooltip-center', true)

      const diffGap = 8
      const marginLeft = barRectWidth / half

      return {
        xScalePosition: rectBarX - diffGap + marginLeft
      }
    }

    // used when it has less bar
    const rectBarXWithGap = rectBarX + 24

    if (rectBarXWithGap > width * ninetyPercent) {
      tooltipElement.classed('bar-chart-tooltip-right', true)

      const diffGap = 4
      const marginRight = widthTooltip / half + barRectWidth / half - diffGap

      return {
        xScalePosition: rectBarX - marginRight
      }
    }

    tooltipElement.classed('bar-chart-tooltip-left', true)

    const diffGap = 4
    const marginLeft = widthTooltip - barRectWidth

    return {
      xScalePosition: rectBarX - diffGap + marginLeft
    }
  }

  function _mountVerticalSvg (svg, svgVertical, svgWrapper, yAxisGenerator, xGroup) {
    if (svgVertical) {
      $timeout(
        () => {
          const groupXPosition = 65
          const verticalGroup = svgVertical
            .append('g')
            .attr('transform', `translate(${groupXPosition}, ${$scope.chart.group.y})`)
            .attr('class', 'y-group-vertical')
          const verticalYAxis = verticalGroup
            .append('g')
            .call(yAxisGenerator)
            .attr('class', 'y-axis-vertical')
          verticalYAxis.selectAll('text').attr('dx', $scope.chart.yAxis.text.dx)

          svg.selectAll('.y-axis .tick text').style('opacity', '0')

          const { width: xGroupWidth } = api._getGroupXWidth(xGroup)
          svg.attr('width', xGroupWidth)
          svgWrapper.style('max-width', `${xGroupWidth}px`)

          const newYAxisGenerator = yAxisGenerator.tickSize(-xGroupWidth)
          svg.selectAll('.y-axis').call(newYAxisGenerator)
        },
        100,
        false
      )
    }
  }

  function _handleResizeBarChart () {
    $window.addEventListener(
      'resize',
      Utils.debounce(() => {
        const { width, x } = checkWidthScreen($window.innerWidth, () => {
          api._loadDataFrameData(slug, $scope.data.id)
        }, $scope.data.size, $scope.fullWidth)

        $scope.chart.width = width
        $scope.chart.group.x = x
      })
    )

    return checkWidthScreen(
      $window.innerWidth,
      null,
      $scope.data.size,
      $scope.fullWidth
    )
  }

  function _getDateFormatByDefault (vars) {
    if (!Object.keys(vars).length) {
      return ''
    }

    if (vars?.list) {
      const dateFormat = vars.list.reduce((acc, item) => {
        if (!acc && item.queryKey === 'dateFormat' && item.defaultValue === 'TRUE') {
          acc = item.queryValue
        }

        return acc
      }, '')

      return dateFormat
    }

    return ''
  }

  function _getDataFormatByDefault (vars) {
    if (!Object.keys(vars).length) {
      return ''
    }

    if (vars?.list) {
      const dataFormat = vars.list.reduce((acc, item) => {
        if (!acc && item.queryKey === 'dataFormat' && item.defaultValue === 'TRUE') {
          acc = item.queryValue
        }

        return acc
      }, '')

      return dataFormat
    }

    return ''
  }

  function _checkLineBreak (data) {
    const { days, hours, minutes } = Utils.formatMinutes(data.total)

    if (days && minutes) {
      return '<br />'
    }

    if (days && hours) {
      return '<br />'
    }

    if (hours && minutes) {
      return '<br />'
    }

    return ''
  }

  function _isHandleDateFormat (calendarDaysDiff) {
    const rangeOfWeekly = 60

    if (calendarDaysDiff > rangeOfWeekly) {
      return 'MONTHLY'
    }

    return 'WEEKLY'
  }

  function _getYScaleDomain (type, response) {
    const defaultDomain = [0, d3Max(response, data => data.total)]

    const types = {
      TIME_DATE: () => defaultDomain,
      TIME_TEXT: () => defaultDomain,
      RATE_DATE: () => {
        const maxValue = response[0].maxValue
        const domain = ['0.0', maxValue.toFixed(1)]

        return domain
      },
      NUMBER_TEXT: () => defaultDomain,
      TEXT: () => defaultDomain
    }

    return types[String(type)]?.() || defaultDomain
  }

  function _insertMetricIntoBar (xGroup, response, xScale, yScale) {
    const half = 2
    const barWidth = $scope.chart.bar.width
    const unitType = $scope.data.unitType

    const halfBarWidth = barWidth / half
    const yGap = 12
    const gapForWithoutValue = 8

    const parsedHeight = 10

    const unitTypes = {
      RATE_DATE: () => {
        const barRatings = xGroup
          .selectAll('.bar-rating')
          .data(response)
          .enter()
          .append('text')
          .attr('class', data => `bar-rating bar-rating-${data.date.getTime()}`)
          .attr('y', data => {
            if (data.total) {
              return yScale(data.total) - yGap
            }

            return yScale(data.total) - parsedHeight - gapForWithoutValue
          })
          .attr('x', data => xScale(data.date))
          .text(data => api._formatNumber(data.total))

        $timeout(() => {
          barRatings.attr('x', data => {
            const barClass = `.bar-${data.date.getTime()}`
            const textClass = `.bar-rating-${data.date.getTime()}`

            return api._getBarMetricXPosition(xGroup, barClass, textClass, halfBarWidth, half)
          })
        }, 100, false)
      },
      NUMBER_TEXT: () => {
        const barRatings = xGroup
          .selectAll('.bar-number')
          .data(response)
          .enter()
          .append('text')
          .attr('class', data => `bar-number bar-number-${data.color.replace('#', '')}`)
          .attr('y', data => {
            if (data.total) {
              return yScale(data.total) - yGap
            }

            return yScale(data.total) - parsedHeight - gapForWithoutValue
          })
          .attr('x', data => xScale(data.color))
          .text(data => api._formatNumber(data.total))

        $timeout(() => {
          barRatings.attr('x', data => {
            const barClass = `.bar-${data.color.replace('#', '')}`
            const textClass = `.bar-number-${data.color.replace('#', '')}`

            return api._getBarMetricXPosition(xGroup, barClass, textClass, halfBarWidth, half)
          })
        }, 100, false)
      },
      TEXT: () => {
        const barTexts = xGroup
          .selectAll('.bar-text')
          .data(response)
          .enter()
          .append('text')
          .attr('class', data => `bar-text bar-text-${data.id}`)
          .attr('y', data => {
            if (data.total) {
              return yScale(data.total) - 25
            }

            return yScale(data.total) - parsedHeight - gapForWithoutValue
          })
          .attr('x', data => xScale(data.id))
          .text(data => api._formatNumber(data.total))

        $timeout(() => {
          barTexts.attr('x', data => {
            const barX = xScale(data.id)
            const barWidth = xScale.bandwidth()
            const textClass = `.bar-text-${data.id}`

            const textNode = xGroup.select(textClass).node()
            if (textNode) {
              const { width: textWidth } = textNode.getBBox()
              return barX + (barWidth - textWidth) / 2
            }

            return barX + barWidth / 2
          })
        }, 100, false)
      }
    }

    if (unitTypes[String(unitType)]) {
      $scope.flags.notHaveBarMetric = false
      return unitTypes[String(unitType)]()
    }

    $scope.flags.notHaveBarMetric = true
  }

  function _getBarMetricXPosition (xGroup, barClass, textClass, halfBarWidth, half) {
    const { x } = xGroup
      .select(barClass)
      .node()
      .getBBox()
    const { width } = xGroup
      .select(textClass)
      .node()
      .getBBox()
    const xPosition = (x - width) + (halfBarWidth + width / half)

    return xPosition
  }

  function _formatNumber (number) {
    if (Number.isInteger(number)) {
      return number.toString()
    }

    const parsedNumber = parseFloat(number)
    return parsedNumber.toFixed(1)
  }

  function _hasXAxisHidden (type) {
    const types = {
      NUMBER_TEXT: true,
      TEXT: false,
      DEFAULT: false
    }

    if (types[String(type)]) {
      return types[String(type)]
    }

    return types.DEFAULT
  }
}

export { AnalyticsDataFrameBarChartController }
